import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import type { Tool } from "@repo/app-types/entities/tool";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { PageWidth } from "@repo/ui/Navigation/pageWidth";
import { SmallTimeStamp } from "@repo/ui/SmallTimeStamp/smallTimeStamp";
import {
  EntityTable,
  type EntityTableColumnDefinition,
} from "@repo/ui/tables/entityTable";
import { showToast, ToastVariant } from "@repo/ui/toast/toast";
import { IconPlus } from "@tabler/icons-react";
import { type FC, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useAllToolsQuery } from "../api/entities/tool.js";
import { appPaths } from "../app/appPaths.js";
import { PageBody, PageLayout } from "../components/layout/pageLayout.js";
import { useDeepLinkedProposal } from "../features/copilot/copilotDeepLinks.js";
import { CreateToolModal } from "../features/tools/createToolModal.js";
import type { NewToolProposal } from "../features/tools/createToolModalDialog.js";
import { Can } from "../helpers/teammateAuthorizationContext.js";

export const ToolsPage: FC = () => {
  const navigate = useNavigate();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const { data: tools, isLoading, isError } = useAllToolsQuery();

  // An Ask Alpharun card proposing a new tool links here (?proposalId=): the
  // tool has no page yet, so the proposal opens the create modal filled in.
  const [proposal, setProposal] = useState<NewToolProposal | undefined>(
    undefined,
  );
  const { row: deepLinkProposal, clear: clearDeepLinkProposalId } =
    useDeepLinkedProposal("tool", {
      onUnavailable: () =>
        showToast({
          variant: ToastVariant.Error,
          title: "This suggestion is no longer available.",
        }),
    });
  useEffect(() => {
    if (!deepLinkProposal) {
      return;
    }
    clearDeepLinkProposalId();
    const { id, data } = deepLinkProposal;
    if (data.kind !== "create_tool") {
      return;
    }
    setProposal({ id, data });
    setIsCreateModalOpen(true);
  }, [deepLinkProposal, clearDeepLinkProposalId]);

  const columns: readonly EntityTableColumnDefinition<Tool>[] = useMemo(
    () => [
      {
        id: "name",
        isRowHeader: true,
        header: "Tool name",
        className: "w-[60%]",
        renderCell: (tool) => {
          return (
            <div className="flex items-center">
              <span className="w-0 flex-auto truncate font-medium">
                {tool.name}
              </span>
            </div>
          );
        },
      },
      {
        id: "type",
        header: "Type",
        className: "w-[20%]",
        renderCell: (tool) => {
          return (
            <div className="flex items-center">
              <span className="w-0 flex-auto truncate text-foreground">
                {tool.type.replace(/_/g, " ")}
              </span>
            </div>
          );
        },
      },
      {
        id: "updated_at",
        header: "Updated",
        className: "w-[20%]",
        renderCell: (tool) => {
          const timestamp = tool.updated_at || tool.created_at;
          return (
            <div className="flex items-center">
              <SmallTimeStamp timestamp={timestamp} className="text-sm" />
            </div>
          );
        },
      },
    ],
    [],
  );

  return (
    <PageLayout>
      <PageHeader
        width={PageWidth.Standard}
        title="Tools"
        actions={
          <Can
            I={PermissionActions.UPDATE}
            a={PermissionSubjects.TEAMMATES}
            passThrough={true}
          >
            {(allowed) => (
              <Button
                size={Size.SMALL}
                variant={ButtonVariant.Primary}
                onClick={() => setIsCreateModalOpen(true)}
                icon={<IconPlus className="h-4 w-4" />}
                isDisabled={!allowed}
              >
                Create tool
              </Button>
            )}
          </Can>
        }
      />

      <PageBody width={PageWidth.Standard}>
        <EntityTable
          aria-label="Tools"
          columns={columns}
          entities={tools ?? []}
          isLoading={isLoading}
          isError={isError}
          emptyMessage="No tools created yet."
          onEntityAction={(tool) => {
            navigate(appPaths.agentTool.build({ toolId: tool.id }));
          }}
        />
      </PageBody>

      <CreateToolModal
        isOpen={isCreateModalOpen}
        proposal={proposal}
        onClose={() => {
          setIsCreateModalOpen(false);
          setProposal(undefined);
        }}
      />
    </PageLayout>
  );
};
